import "../Pages.scss";
import "./TermsOfUse.scss";
import "../../styles/variables.scss";
import "../../styles/global.scss";

/* ========= Reusable blocks (no CSS changes) ========= */
function PageTitle({ children }) {
  return <div className="Fu-70s-70l">{children}</div>;
}

function Section({ title, children }) {
  return (
    <div className="section-wrapper">
      <div className="Ft-24s-24l fw600">{title}</div>
      <div className="section-text Ft-16s-24l">{children}</div>
    </div>
  );
}

function Subtitle({ children }) {
  return <div className="Ft-18s-24l fw600 subtitle">{children}</div>;
}

function Paragraph({ children }) {
  return <div className="Ft-16s-24l">{children}</div>;
}

function BulletList({ items }) {
  if (!items?.length) return null;
  return (
    <ul>
      {items.map((t, i) => (
        <li className="info-list Ft-16s-24l" key={i}>
          {t}
        </li>
      ))}
    </ul>
  );
}

/* ================= Page ================= */
export default function TermsOfUse() {
  return (
    <div className="tp-tos-main-container">
      <div className="tp-tos-main-wrapper max-width">
        <PageTitle>Terms Of Use</PageTitle>

        {/* 1.0 */}
        <Section title={"1.0 \tAcceptance of Terms"}>
          <Paragraph>
            {
              'These Terms of Use ("Terms") constitute a legally binding agreement between you and Torum International Sdn. Bhd. ("TorumPay"). For the purposes of these Terms, references to “TorumPay,” “we,” “us,” or “our” shall mean Torum International Sdn. Bhd. References to “you,” “your,” or “User” shall mean any individual or entity accessing or using TorumPay’s services.'
            }
          </Paragraph>
          <Paragraph>
            {
              "By registering and opening an account on TorumPay (each a “User Account”), you confirm that you are eligible to do so and unconditionally agree that you have read, understood, and accept all the Terms set forth herein, together with the Privacy Policy and Risk Disclosure Statement (collectively, the “Agreement”)."
            }
          </Paragraph>
          <Paragraph>
            {
              "You further acknowledge that it is your sole responsibility to understand and comply with all applicable laws, regulations, and requirements in Malaysia or any other relevant jurisdiction that may govern your use of TorumPay’s services."
            }
          </Paragraph>
          <Paragraph>
            {
              "TorumPay reserves the right, at its sole discretion, to refuse to open or maintain a User Account for any individual or entity without providing any reason."
            }
          </Paragraph>
          <Paragraph>
            {
              "Please read these Terms carefully, as they affect your legal rights and obligations when using TorumPay’s platform."
            }
          </Paragraph>
        </Section>

        {/* 2.0 */}
        <Section title={"2.0 \tAmendments and Modifications"}>
          <Paragraph>
            {
              "TorumPay reserves the right to update, modify, or amend these Terms at any time by publishing the revised version on our website. Amendments will take effect immediately upon publication, and by continuing to use TorumPay’s services, you acknowledge and accept the changes. You are responsible for reviewing our website regularly to stay informed of updates. If a modification materially affects your rights, we will make reasonable efforts to notify you via email or platform notifications. If you disagree with any amendments, your only recourse is to discontinue using our services and close your account. TorumPay will not be liable for any loss or damage arising from amendments to these Terms."
            }
          </Paragraph>
        </Section>

        {/* 3.0 */}
        <Section title={"3.0 \tDefinitions"}>
          <BulletList
            items={[
              <>
                <span className="fw600">“TorumPay”</span> refers to Torum
                International Sdn. Bhd., including its affiliates, subsidiaries,
                and any related entities involved in the operation of the
                platform.
              </>,
              <>
                <span className="fw600">“User”</span> refers to any individual,
                corporation, or legal entity that accesses or uses TorumPay’s
                services, whether registered or unregistered.
              </>,
              <>
                <span className="fw600">“Digital Asset”</span> refers to any
                form of digital currency, cryptocurrency, token, or other
                cryptographic asset available for trade, transfer, or custody on
                TorumPay.
              </>,
              <>
                <span className="fw600">“Fiat Currency”</span> refers to
                government-issued legal tender that is accepted in exchange for
                digital assets, including but not limited to Malaysian Ringgit
                (MYR) and other supported currencies.
              </>,
              <>
                <span className="fw600">“Wallet”</span> refers to the digital
                storage solution provided by TorumPay for holding, transferring,
                and managing digital assets.
              </>,
              <>
                <span className="fw600">“Transaction”</span> refers to any
                deposit, withdrawal, transfer, trade, or exchange of digital
                assets conducted through TorumPay’s platform.
              </>,
              <>
                <span className="fw600">“KYC” (Know Your Customer)</span> refers
                to the verification process required by TorumPay to authenticate
                user identity and comply with Anti-Money Laundering (AML)
                regulations.
              </>,
              <>
                <span className="fw600">“AML” (Anti-Money Laundering)</span>{" "}
                refers to the regulatory measures and compliance standards
                followed by TorumPay to prevent financial crimes, including
                money laundering and terrorism financing.
              </>,
              <>
                <span className="fw600">“Sanctions”</span> refer to any trade or
                economic restrictions imposed by international bodies,
                regulatory authorities, or government agencies that may affect a
                user’s ability to use TorumPay’s services.
              </>,
              <>
                <span className="fw600">“Restricted Jurisdictions”</span> refer
                to countries or territories where TorumPay’s services are not
                available due to regulatory or compliance reasons.
              </>,
            ]}
          />
        </Section>

        {/* 4.0 */}
        <Section title={"4.0\tUser Eligibility & Account Restrictions"}>
          <Paragraph>
            {
              "By registering for a TorumPay account, you expressly represent and warrant that:"
            }
          </Paragraph>
          <BulletList
            items={[
              "You are at least 18 years of age and have full legal capacity to accept these Terms and enter into transactions available on the TorumPay platform. If you are registering on behalf of a business entity, you confirm that you have the authority to bind the entity to these Terms.",
              "You will only open and maintain one TorumPay account in your name. Multiple accounts for the same individual are strictly prohibited unless explicitly approved by TorumPay.",
              "If you seek to open a business account on behalf of a legal entity (e.g., company, trust, or partnership), you must follow the TorumPay Business Account application process, which may require additional verification and documentation.",
              "You agree to provide accurate, up-to-date, and complete information during the registration and verification process. You also agree to promptly update any changes to your information.",
              "You are responsible for safeguarding your account credentials, including passwords and security keys. You must notify TorumPay immediately of any unauthorized access or security breaches.",
              "You will ensure that sufficient fiat or digital assets are available in your account before initiating any transaction. TorumPay is not responsible for failed transactions due to insufficient funds.",
              "You will not deposit or withdraw funds to or from a third-party account unless explicitly permitted by TorumPay’s policies. All transactions must comply with TorumPay’s Anti-Money Laundering (AML) and Know Your Customer (KYC) regulations.",
              "You must not use your account for unlawful activities, including but not limited to fraud, money laundering, terrorist financing, unauthorized financial services, or transactions that violate any applicable laws, regulations, or sanctions requirements.",
              "You agree that TorumPay reserves the right to conduct Enhanced Due Diligence (EDD) on your account at any time. If you fail to provide additional requested information, TorumPay may suspend or terminate your account.",
              "TorumPay reserves the right to suspend, restrict, or terminate any account at its sole discretion if it determines that the account is being used in a manner inconsistent with these Terms, applicable laws, or for activities deemed high-risk or suspicious.",
              "TorumPay is not liable for any losses incurred due to account restrictions, suspensions, or terminations arising from violations of these Terms or applicable laws.",
            ]}
          />
        </Section>

        {/* 5.0 */}
        <Section title={"5.0\tRisks"}>
          <Paragraph>
            {
              "Engaging with digital assets through TorumPay carries significant risks. Before using our platform, you must review this section alongside the Self-Declared Risk Acknowledgment to fully understand the potential financial and operational hazards."
            }
          </Paragraph>
          <BulletList
            items={[
              "Digital assets are not classified as legal tender in Malaysia. Consequently, losses in value or ownership are not insured or protected by government-backed compensation schemes such as Perbadanan Insurans Deposit Malaysia (PIDM) or the Capital Market Compensation Fund.",
              "The value of digital assets is highly unpredictable. Market sentiment, liquidity constraints, technological advancements, regulatory shifts, and macroeconomic events can all contribute to extreme price volatility. Users should be prepared for the possibility of significant financial losses, including the total devaluation of their holdings.",
              "Blockchain transactions are irreversible. Errors in wallet addresses, unauthorized access, or fraud may lead to asset loss with no recourse for recovery. Users bear sole responsibility for safeguarding their funds, verifying transaction details, and implementing necessary security measures to prevent unauthorized access.",
              "Unlike conventional financial instruments, digital assets lack institutional backing. The absence of regulatory oversight in certain jurisdictions means users may have limited legal protections. Additionally, changes in legislation, security breaches, or the emergence of superior technologies could negatively impact the value or usability of digital assets.",
              "TorumPay implements industry-standard security protocols, including cold storage mechanisms and encryption safeguards, to protect user funds. However, risks such as cyber threats, third-party failures, system outages, and liquidity disruptions remain inherent to digital asset trading. TorumPay does not assume liability for losses arising from these risks.",
              "Users should carefully evaluate their financial capacity and risk tolerance before engaging in digital asset transactions. TorumPay does not provide investment, legal, or tax advice. Consulting professional advisors is strongly recommended before making trading decisions.",
            ]}
          />
        </Section>

        {/* 6.0 */}
        <Section title={"6.0\tElectronic Communications"}>
          <Paragraph>
            {
              "By using TorumPay, you consent to receive all communications from us electronically. This includes but is not limited to account notifications, transaction confirmations, legal disclosures, policy updates, and promotional materials. We may communicate with you via email, SMS, push notifications, or by posting updates on our platform."
            }
          </Paragraph>
          <Paragraph>
            {
              "It is your responsibility to ensure that the contact details associated with your TorumPay account are accurate and up to date. If you fail to receive or review electronic communications, TorumPay shall not be held responsible for any resulting consequences, including missed deadlines or service disruptions."
            }
          </Paragraph>
          <Paragraph>
            {
              "TorumPay reserves the right to determine the format and frequency of electronic communications. In some cases, we may require you to acknowledge receipt of important notices. Failure to do so may result in restricted access to certain services until confirmation is received."
            }
          </Paragraph>
          <Paragraph>
            {
              "If you wish to withdraw consent for electronic communications, you must submit a written request to TorumPay’s support team. However, opting out of electronic communications may result in the suspension or termination of your account, as certain communications are legally required for service continuity."
            }
          </Paragraph>
          <Paragraph>
            {
              "By continuing to use TorumPay’s services, you agree that all electronic communications from us satisfy any legal requirement that such communications be in writing."
            }
          </Paragraph>
        </Section>

        {/* 7.0 */}
        <Section title={"7.0\tIdentity Verification (KYC/AML)"}>
          <Paragraph>
            {
              "To comply with Anti-Money Laundering (AML) and Counter-Terrorism Financing (CTF) regulations, all users must complete Know Your Customer (KYC) verification before accessing any TorumPay services. This process ensures that TorumPay maintains a secure and compliant platform, preventing illicit financial activities such as fraud, money laundering, and terrorism financing. By using TorumPay, you agree to provide accurate and up-to-date identification information as part of our commitment to regulatory compliance."
            }
          </Paragraph>
          <Paragraph>
            {
              "Users must submit personal identification details, including but not limited to full legal name, date of birth, residential address, and a government-issued identification document (such as a passport, national ID, or driver’s license). In certain circumstances, TorumPay may conduct Enhanced Due Diligence (EDD) by requesting additional information and documentation, such as proof of address, source of funds, and other relevant details. If users fail to complete the required verification or provide misleading information, TorumPay reserves the right to suspend or terminate their accounts without prior notice."
            }
          </Paragraph>
          <Paragraph>
            {
              'Ongoing monitoring will be conducted to detect suspicious activity, and any account deemed to be engaging in prohibited activities may face immediate restrictions. Additionally, users from jurisdictions listed under the "Restricted Jurisdictions" section are not permitted to access or use TorumPay’s services.TorumPay retains the right to update the list of restricted countries based on regulatory changes.'
            }
          </Paragraph>
          <Paragraph>
            {
              "All user data collected during KYC verification is securely stored and managed in compliance with Malaysia’s Personal Data Protection Act (PDPA). TorumPay will not disclose your information to third parties except as required by law or regulatory authorities. By using TorumPay, you acknowledge and accept the identity verification process and agree to cooperate with any additional compliance measures deemed necessary. Failure to meet these requirements may result in account suspension, fund freezes, or reporting to relevant authorities."
            }
          </Paragraph>
          <Paragraph>
            {
              "To exercise any of these rights, please contact us using the details provided in Section 10."
            }
          </Paragraph>
        </Section>

        {/* 8.0 */}
        <Section title={"8.0\tDigital Asset Wallet Service"}>
          <Paragraph>
            {
              "TorumPay provides users with a digital asset wallet to send, receive, and store supported digital assets (“TorumPay Wallet Transactions”). The list of supported digital assets is determined solely by TorumPay and may be updated periodically. Users must ensure they transact only with supported digital assets, as sending unsupported assets may lead to irreversible loss."
            }
          </Paragraph>

          <Subtitle>
            8.1 Transaction Processing &amp; Network Confirmation
          </Subtitle>
          <Paragraph>
            {
              "When users initiate a digital asset transaction, it must be confirmed and recorded on the respective blockchain network (e.g., Bitcoin or Ethereum). TorumPay does not own or control these networks and cannot alter or reverse transactions once submitted. Pending transactions remain unconfirmed until validated by the network, during which funds will be temporarily unavailable in the user’s TorumPay Wallet."
            }
          </Paragraph>
          <Paragraph>
            {
              "By using TorumPay to send digital assets, users authorize TorumPay to submit their transaction requests to the relevant blockchain. Once submitted, the transaction is processed automatically by the network and cannot be canceled or modified. TorumPay is not liable for delays or failures caused by network congestion, protocol changes, or unforeseen blockchain disruptions."
            }
          </Paragraph>

          <Subtitle>8.2 Supported Digital Assets &amp; Protocols</Subtitle>
          <Paragraph>
            {
              "TorumPay supports only officially listed digital assets. Users must not attempt to transact unsupported assets or derivative protocols (e.g., forked chains, metacoins, or staking mechanisms) unless explicitly supported by TorumPay. Unsupported assets sent to a TorumPay Wallet may be permanently lost, and TorumPay is not responsible for their recovery."
            }
          </Paragraph>
          <Paragraph>
            {
              "Blockchain protocols operate independently of TorumPay and may be subject to modifications, known as “forks.” Such changes can impact the value, functionality, or structure of digital assets. In case of a fork, TorumPay reserves the right to suspend operations and determine whether to support the new protocol branch. If TorumPay chooses not to support a forked asset, users will have no claim over those assets."
            }
          </Paragraph>

          <Subtitle>8.3 Receiving Digital Assets &amp; Network Fees</Subtitle>
          <Paragraph>
            {
              "Users can receive digital assets by sharing a unique wallet address generated in their TorumPay Wallet. To avoid loss of funds, users must ensure that digital assets are sent to the correct wallet address corresponding to the supported asset type (e.g., BTC to a BTC address)."
            }
          </Paragraph>
          <Paragraph>
            {
              "Blockchain transactions are subject to network fees, which may vary based on network congestion. TorumPay does not control these fees or guarantee transaction processing times. Users acknowledge that blockchain transactions may be delayed or fail due to external factors beyond TorumPay’s control."
            }
          </Paragraph>

          <Subtitle>8.4 Funds Sent or Received in Error</Subtitle>
          <Paragraph>
            {
              "If users receive digital assets in error, they must promptly notify TorumPay and return the funds as instructed. Users have no legal claim to mistakenly receive assets."
            }
          </Paragraph>
          <Paragraph>
            {
              "If users mistakenly send digital assets (e.g., incorrect address, wrong network, missing transaction memo), TorumPay may, at its discretion, assist with recovery. However, recovery is not guaranteed, and TorumPay is not liable for losses resulting from user errors. Additional processing fees may apply for recovery attempts."
            }
          </Paragraph>
        </Section>

        {/* 9.0 */}
        <Section title={"9.0\tFiat Deposit "}>
          <Paragraph>
            {
              "To fund your TorumPay Wallet, users must complete the Know Your Customer (KYC) verification process. Deposits must be made from a bank account registered in the user’s name, using one of TorumPay’s supported payment methods. Deposits made through cash, cheques, or third-party accounts will not be accepted and may be rejected or returned at the sender’s expense."
            }
          </Paragraph>
          <Paragraph>
            {
              "Each deposit must include a unique reference code provided by TorumPay to ensure correct allocation. Failure to include this reference may result in processing delays or non-allocation. Deposits are subject to standard bank processing times, and TorumPay is not liable for delays caused by financial institutions."
            }
          </Paragraph>
          <Paragraph>
            {
              "If a deposit fails to meet the required criteria or remains unallocated for more than 14 days, TorumPay reserves the right to handle the funds in accordance with its internal policies. Users acknowledge that any unallocated deposits below the minimum retrievable amount may be forfeited."
            }
          </Paragraph>
        </Section>

        {/* 10.0 */}
        <Section title={"10.0\tFiat Withdrawal  "}>
          <Paragraph>
            {
              "Users can request withdrawals only to their verified bank account. Third-party withdrawals or transfers to accounts not registered under the user’s name are strictly prohibited. Users must ensure that all withdrawal instructions, including bank account details and requested amounts, are accurate. TorumPay is not responsible for losses due to incorrect withdrawal details provided by the user."
            }
          </Paragraph>
          <Paragraph>
            {
              "Withdrawal processing times vary depending on banking operations, regulatory compliance checks, and system availability. While TorumPay aims to process withdrawals promptly, no guarantees are provided regarding the exact time of fund availability in the recipient’s account."
            }
          </Paragraph>
          <Paragraph>
            {
              "TorumPay reserves the right to impose minimum and maximum withdrawal limits, as well as additional verification steps for larger transactions. If suspicious activity is detected, withdrawal requests may be delayed, restricted, or subject to further compliance reviews."
            }
          </Paragraph>
          <Paragraph>
            {
              "By using TorumPay’s deposit and withdrawal services, users acknowledge and accept all associated risks, including processing delays, regulatory requirements, and potential restrictions due to compliance measures."
            }
          </Paragraph>
        </Section>

        {/* 11.0 */}
        <Section title={"11.0 Instant Buy or Sell (Digital Broker Model)"}>
          <Paragraph>
            {
              "Users can access the Instant Buy or Sell feature to purchase or sell digital assets at a quoted exchange rate."
            }
          </Paragraph>
          <Paragraph>
            {"By using this service, you acknowledge and agree that:"}
          </Paragraph>
          <BulletList
            items={[
              "Once you place an instant buy or sell order, TorumPay will facilitate the transaction by matching your order with available offers on other Digital Asset exchanges. The trade will be executed in accordance with your instructions, ensuring a seamless and efficient process.",
              "The quoted exchange rate is subject to market fluctuations and may differ from rates displayed elsewhere. By proceeding, you accept the quoted rate.",
              "Once executed, orders cannot be reversed. It is the user’s responsibility to verify transaction details before confirming.",
              "Transaction limits apply per order, as detailed in the Fees section.",
              "TorumPay does not guarantee the availability of the Instant Buy or Sell service and is not liable for any delays, disruptions, or system maintenance issues affecting transactions.",
              "TorumPay reserves the right to delay or reject transactions of fraud, illegal activity, or errors suspected. TorumPay holds no liability for any cancellations or delays due to these security measures.",
              "By proceeding with an Instant Buy or Sell transaction, you confirm that you understand and accept these terms.",
            ]}
          />
        </Section>

        {/* 12.0 */}
        <Section title={"12.0\tFees & Charges"}>
          <Paragraph>
            {
              "The fees applicable to transactions on TorumPay can be found under the Fees & Charges section on our website. TorumPay reserves the right to modify fees at any time and will update the Fees & Charges page accordingly to reflect any changes."
            }
          </Paragraph>
        </Section>

        {/* 13.0 */}
        <Section title={"13.0\tAccount Security"}>
          <Paragraph>
            {
              "TorumPay prioritizes the security of your TorumPay Account, but users are fully responsible for safeguarding their credentials. You must:"
            }
          </Paragraph>
          <BulletList
            items={[
              "Maintain strict control over your login details, including passwords and authentication methods.",
              "Enable Two-Factor Authentication (2FA) to enhance account protection.",
              "Keep your contact information updated to receive security alerts.",
              "Secure your email, phone number, and authentication devices linked to your account.",
            ]}
          />
          <Paragraph>
            {
              "Failure to follow these measures may result in unauthorized access, theft, or loss of digital assets. TorumPay will not be liable for security breaches caused by user negligence."
            }
          </Paragraph>
          <Paragraph>
            {
              "Users should remain vigilant against phishing, malware, and fraudulent messages impersonating TorumPay. We strongly recommend using antivirus software and verifying all communications directly through the official TorumPay website. If you suspect unauthorized access, immediately report the incident to TorumPay’s support team."
            }
          </Paragraph>
          <Paragraph>
            {
              "By using TorumPay’s services, you acknowledge full responsibility for all activity within your account, whether authorized or unauthorized."
            }
          </Paragraph>
        </Section>

        {/* 14.0 */}
        <Section title={"14.0\tService Availability & Platform Downtime"}>
          <Paragraph>
            {
              "While TorumPay strives to maintain uninterrupted access to its services, we do not guarantee continuous availability of our website, TorumPay Account, or related services. There may be delays, interruptions, or technical issues affecting accessibility. TorumPay makes no guarantees regarding error-free operation, and users acknowledge that service disruptions may occur."
            }
          </Paragraph>
        </Section>

        {/* 15.0 */}
        <Section title={"15.0\tAccount Inactivity"}>
          <Paragraph>
            {
              "Local Currency deposited into your TorumPay Wallet may only be used for purchasing supported digital assets or withdrawing to an approved bank account. The wallet is not intended for storing Local Currency. If no transactions are made on an account holding Local Currency for six consecutive months, a maintenance fee of RM10 (or equivalent) will be deducted every six months. Continued inactivity may lead to account restrictions or suspension at TorumPay’s discretion."
            }
          </Paragraph>
          <Paragraph>
            {
              "In the event funds are held in your TorumPay Wallet with no recorded activity for two years and we are unable to contact you, applicable laws may require us to report and transfer such funds to the relevant authorities as unclaimed property. Users are encouraged to maintain active engagement with their accounts to avoid such fees or limitations."
            }
          </Paragraph>
        </Section>

        {/* 16.0 */}
        <Section title={"16.0 Account Closure, Suspension & Termination"}>
          <Paragraph>
            {
              "Users may request to close their TorumPay account at any time, provided there are no pending transactions, disputes, or outstanding obligations. To initiate closure, users must withdraw all funds and submit a formal request through TorumPay’s platform. "
            }
          </Paragraph>
          <Paragraph>
            {
              "TorumPay reserves the right to suspend or terminate accounts at its sole discretion, including but not limited to cases involving suspected fraud, violation of terms, non-compliance with KYC/AML requirements, or inactivity. In cases of suspension, users may be required to provide additional verification or clarification before regaining access."
            }
          </Paragraph>
          <Paragraph>
            {
              "If an account remains inactive for an extended period, TorumPay may impose maintenance fees or classify it as dormant, potentially leading to termination. Any undrawn assets or proceeds from a terminated account will be retained within the exchange for a period of two years. After this period, any unclaimed assets shall be liquidated and surrendered to the Registrar of Unclaimed Moneys, in accordance with the provisions of the Malaysian Unclaimed Moneys Act 1965."
            }
          </Paragraph>
          <Paragraph>
            {
              "TorumPay shall not be liable for any losses incurred due to account suspension or termination, and users will be notified of any actions taken against their account unless prohibited by law or regulatory obligations."
            }
          </Paragraph>

          <Subtitle>16.1 Death of Account Holder</Subtitle>
          <Paragraph>
            {
              "In the event of an account holder's death, TorumPay may restrict or suspend the account for security reasons. No transactions will be processed until a designated executor or fiduciary provides satisfactory legal documentation. The executor may request either the liquidation of digital assets for withdrawal in local currency or the transfer of digital assets to another wallet."
            }
          </Paragraph>
          <Paragraph>
            {
              "If no executor is designated, TorumPay reserves the right to determine the rightful beneficiary based on applicable laws. Additional legal documentation may be required, and if there are disputes, TorumPay may require a court order before taking any action."
            }
          </Paragraph>
        </Section>

        {/* 17.0 */}
        <Section title={"17.0 Use of API"}>
          <Paragraph>
            {
              "Users are fully responsible for securing their API keys, passwords, and credentials, and any requests made through the API—whether authorized or not—are at the user's sole risk. TorumPay enforces rate limits and fair usage policies, and any attempt to bypass these controls or engage in activities that negatively impact the platform may result in suspension or termination of API access. Additionally, any market data provided through the API must not be redistributed or sold without prior written approval from TorumPay."
            }
          </Paragraph>
        </Section>

        {/* 18.0 */}
        <Section title={"18.0 Financial Advice Disclaimer  "}>
          <Paragraph>
            {
              "TorumPay does not provide financial, investment, or tax advice. All information, tools, or services offered through TorumPay are for informational purposes only and should not be considered as recommendations to buy, sell, or hold any digital asset. Users are solely responsible for their financial decisions and should consult independent financial, legal, or tax advisors before engaging in digital asset transactions. Users are also responsible for determining and fulfilling their own tax obligations, including reporting and paying any taxes due on digital asset transactions in accordance with their local laws."
            }
          </Paragraph>
        </Section>

        {/* 19.0 */}
        <Section title={"19.0\tProhibited Activities "}>
          <Paragraph>
            {
              "Users of TorumPay must use the platform only for lawful and legitimate purposes. The following activities are strictly prohibited:"
            }
          </Paragraph>
          <BulletList
            items={[
              <>
                <span className="fw600">Illegal Transactions:</span> Engaging in
                money laundering, terrorism financing, fraud, tax evasion, or
                other unlawful activities.
              </>,
              <>
                <span className="fw600">Third-Party Use:</span> Using your
                account to conduct transactions on behalf of another individual
                or entity without explicit authorization.
              </>,
              <>
                <span className="fw600">Unauthorized Account Access:</span>{" "}
                Sharing account credentials, allowing third parties to operate
                your account, or attempting unauthorized access to other
                accounts or systems.
              </>,
              <>
                <span className="fw600">Fraudulent Conduct:</span> Providing
                false or misleading information, impersonating another person or
                entity, or using TorumPay to facilitate deception.
              </>,
              <>
                <span className="fw600">Cybersecurity Violations:</span>{" "}
                Uploading malware, hacking, disrupting network security, or
                attempting unauthorized access to TorumPay’s infrastructure.
              </>,
              <>
                <span className="fw600">Restricted Jurisdictions:</span>{" "}
                Accessing TorumPay’s services from a country listed in
                Restricted Jurisdictions or using VPNs to bypass geographic
                restrictions.
              </>,
              <>
                <span className="fw600">Sanctions Violations:</span> Conducting
                transactions with individuals, businesses, or entities subject
                to economic sanctions.
              </>,
              <>
                <span className="fw600">Abuse and Exploitation:</span>{" "}
                Exploiting system vulnerabilities, circumventing security
                measures, or engaging in activities that disrupt TorumPay’s
                services.
              </>,
              <>
                <span className="fw600">Unauthorized Financial Services:</span>{" "}
                Using TorumPay as a payment processor, money transmitter, or
                financial intermediary without authorization.
              </>,
              <>
                <span className="fw600">Unapproved Business Use:</span> Using a
                personal account for commercial or business purposes without
                appropriate registration.
              </>,
            ]}
          />
          <Paragraph>
            {
              "TorumPay reserves the right to investigate suspected violations and take necessary actions, including account suspension, termination, and reporting to authorities."
            }
          </Paragraph>
        </Section>

        {/* 20.0 */}
        <Section title={"20.0 Restricted Jurisdictions"}>
          <Paragraph>
            {
              "TorumPay does not provide services to users residing in or accessing the platform from jurisdictions that are subject to regulatory restrictions, sanctions, or other prohibitions. Users from these restricted jurisdictions are strictly prohibited from opening an account, transacting, or using any services offered by TorumPay."
            }
          </Paragraph>
          <Paragraph>
            {
              "TorumPay reserves the right to update the list of restricted jurisdictions at its discretion, in compliance with applicable laws and regulations. Users must ensure they are legally permitted to use TorumPay’s services in their respective locations. Attempting to bypass these restrictions via VPNs or other means may result in account suspension or termination."
            }
          </Paragraph>
        </Section>

        {/* 21.0 */}
        <Section title={"21.0 Liability & Disclaimers"}>
          <Paragraph>
            {
              'TorumPay provides its services on an "as-is" and "as-available" basis, without warranties of any kind, whether express or implied. While we take reasonable measures to maintain platform security and functionality, we do not guarantee uninterrupted or error-free access.'
            }
          </Paragraph>
          <Paragraph>
            {
              "TorumPay shall not be liable for any direct, indirect, incidental, or consequential losses arising from the use of its services, including but not limited to market fluctuations, unauthorized access, or system failures. Users acknowledge that they assume all risks related to digital asset transactions and should seek independent professional advice when necessary."
            }
          </Paragraph>
        </Section>

        {/* 22.0 */}
        <Section title={"22.0\tIndemnity"}>
          <Paragraph>
            {
              "By using TorumPay's services, you agree to indemnify, defend, and hold harmless TorumPay, its affiliates, directors, employees, and agents from any claims, losses, damages, liabilities, costs, and expenses (including legal fees) arising from your violation of these Terms of Use, applicable laws, or any misuse of the platform."
            }
          </Paragraph>
          <Paragraph>
            {
              "This includes but is not limited to unauthorized account access, fraudulent activities, failure to comply with regulatory obligations, or any action that causes harm to TorumPay or its users. Your indemnification obligation shall survive the termination of your account or use of our services."
            }
          </Paragraph>
        </Section>

        {/* 23.0 */}
        <Section title={"23.0 Dispute Resolution & Arbitration"}>
          <Paragraph>
            {
              "Any dispute, controversy, or claim arising out of or relating to these Terms shall first be attempted to be resolved amicably through discussions between the parties within 30 days of notification of the dispute."
            }
          </Paragraph>
          <Paragraph>
            {
              "If an amicable resolution is not reached, the dispute shall be referred to arbitration in Malaysia, conducted in accordance with the Arbitration Act 2005."
            }
          </Paragraph>
          <Paragraph>
            {
              "The arbitration shall be conducted in English, and the decision of the arbitrator shall be final and binding on both parties. This clause shall survive the termination of your relationship with TorumPay."
            }
          </Paragraph>
        </Section>

        {/* 24.0 */}
        <Section title={"24.0 Intellectual Property Rights"}>
          <Paragraph>
            {
              "All content, trademarks, logos, software, and other materials provided by TorumPay, including but not limited to the website, platform, mobile applications, and any related services, are the exclusive property of TorumPay or its licensors. These materials are protected under Malaysian intellectual property laws and international treaties."
            }
          </Paragraph>
          <Paragraph>
            {
              "You are granted a limited, non-exclusive, non-transferable, and revocable license to access and use TorumPay’s services strictly for personal or business purposes in accordance with these Terms. You may not copy, modify, distribute, sell, sublicense, reverse-engineer, or exploit any part of TorumPay’s intellectual property without prior written consent."
            }
          </Paragraph>
          <Paragraph>
            {
              "Any unauthorized use of TorumPay’s intellectual property may result in legal action. TorumPay reserves all rights not expressly granted under these Terms."
            }
          </Paragraph>
        </Section>

        {/* 25.0 */}
        <Section title={"25.0 Third-Party Links & Services"}>
          <Paragraph>
            {
              "TorumPay’s platform may contain links to third-party websites, applications, or services that are not owned, operated, or controlled by TorumPay. These links are provided for convenience only, and TorumPay does not endorse, review, or assume any responsibility for the content, security, privacy policies, or practices of third-party services."
            }
          </Paragraph>
          <Paragraph>
            {
              "By accessing third-party links or using third-party services through TorumPay, you acknowledge and agree that any transactions, communications, or interactions with such third parties are solely between you and the third party. TorumPay shall not be liable for any loss, damage, or disputes arising from your use of third-party services."
            }
          </Paragraph>
          <Paragraph>
            {
              "We encourage users to review the terms and privacy policies of any third-party services before engaging with them. Your use of such services is at your own risk."
            }
          </Paragraph>
        </Section>

        {/* 26.0 */}
        <Section title={"26.0 Miscellaneous"}>
          <Paragraph>
            {
              "This Terms of Use constitutes the entire agreement between you and TorumPay regarding the use of our services and supersedes any prior agreements, understandings, or representations, whether written or oral."
            }
          </Paragraph>
          <Paragraph>
            {
              "If any provision of this Terms of Use is found to be invalid, unlawful, or unenforceable, the remaining provisions shall continue to be valid and enforceable to the fullest extent permitted by law. TorumPay’s failure to enforce any right or provision under this Terms of Use shall not be considered a waiver of such right or provision."
            }
          </Paragraph>
          <Paragraph>
            {
              "TorumPay may assign or transfer its rights and obligations under these Terms of Use to any affiliated entity or in connection with a merger, acquisition, or sale of assets. You may not assign or transfer your rights or obligations under this Terms of Use without prior written consent from TorumPay."
            }
          </Paragraph>
          <Paragraph>
            {
              "This agreement shall be governed by and construed in accordance with the laws of Malaysia. Any notices or communications required under this Terms of Use shall be delivered via email or through official announcements on TorumPay’s platform."
            }
          </Paragraph>
        </Section>
      </div>
    </div>
  );
}
